import { DatePipe, registerLocaleData } from "@angular/common";
import localeAr from "@angular/common/locales/ar";
import localeFr from "@angular/common/locales/fr";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  HttpClientModule,
  provideHttpClient,
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  ApplicationConfig,
  importProvidersFrom,
  Injector,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withInMemoryScrolling } from "@angular/router";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import {
  KeycloakAngularModule,
  KeycloakBearerInterceptor,
  KeycloakService,
} from "keycloak-angular";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../environments/environment";
import { routes } from "./app.routes";
import { statusRoutes } from "./features/status/status.routes";
import { isStatusHost } from "./shared/utils/status-host";
import { UserApiService } from "./services/generated/core-api/services/user-api.service";
import { RestHttpErrorsInterceptor } from "./shared/interceptors/rest-http-errors-interceptor";
import { HttpLoaderFactory } from "./shared/shared.module";
import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import { AngularFirestoreModule } from "@angular/fire/compat/firestore";
import { AngularFireMessagingModule } from "@angular/fire/compat/messaging";
import { AuthService } from "./services/auth/auth.service";
import { keycloakRedirectUri, rememberKeycloakReturnUrl } from "./services/auth/keycloak-redirect.util";

// Each generated folder has its own ApiConfiguration class = its own DI token.
// We alias them here to avoid name collisions and inject the correct URL per env.
import { ApiConfiguration as ErpApiConfig } from "./services/generated/erp-api/api-configuration";
import { ApiConfiguration as CoreApiConfig } from "./services/generated/core-api/api-configuration";
import { ApiConfiguration as WabaApiConfig } from "./services/generated/waba-api/api-configuration";
import { ApiConfiguration as WabaBotApiConfig } from "./services/generated/waba-bot-api/api-configuration";
import { ApiConfiguration as PaymentApiConfig } from "./services/generated/payment-api/api-configuration";

registerLocaleData(localeFr);
registerLocaleData(localeAr);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      // On the public status domain only the status page exists, at "/".
      isStatusHost() ? statusRoutes : routes,
      withInMemoryScrolling({ scrollPositionRestoration: "top" }),
    ),
    provideHttpClient(),
    provideAnimations(),
    DatePipe,
    importProvidersFrom(KeycloakAngularModule, HttpClientModule),
    {
      provide: APP_INITIALIZER,
      deps: [KeycloakService, Injector],
      useFactory: initializeKeycloak,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KeycloakBearerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RestHttpErrorsInterceptor,
      multi: true,
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: environment.defaultLang,
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    importProvidersFrom([
      AngularFireModule.initializeApp(environment.firebase),
      AngularFireAuthModule,
      AngularFirestoreModule,
      AngularFireMessagingModule,
    ]),

    // ─── API base URL injection ───────────────────────────────────────────
    // These providers override the hardcoded rootUrl in each generated
    // api-configuration.ts. Angular DI picks these up at runtime, so the
    // correct URL is used for whichever environment was built.
    // Angular's fileReplacements (angular.json) ensures the right
    // environment.ts is bundled at build time.
    // Use factories to return actual ApiConfiguration instances.
    // This avoids any edge-cases where code expects a class instance (not just a {rootUrl} object).
    {
      provide: ErpApiConfig,
      useFactory: () =>
        Object.assign(new ErpApiConfig(), { rootUrl: environment.erpApiUrl }),
    },
    {
      provide: CoreApiConfig,
      useFactory: () =>
        Object.assign(new CoreApiConfig(), { rootUrl: environment.coreApiUrl }),
    },
    {
      provide: WabaApiConfig,
      useFactory: () =>
        Object.assign(new WabaApiConfig(), { rootUrl: environment.wabaApiUrl }),
    },
    {
      provide: WabaBotApiConfig,
      useFactory: () =>
        Object.assign(new WabaBotApiConfig(), {
          rootUrl: environment.wabaBotApiUrl,
        }),
    },
    {
      provide: PaymentApiConfig,
      useFactory: () =>
        Object.assign(new PaymentApiConfig(), {
          rootUrl: environment.paymentApiUrl,
        }),
    },
    // ─────────────────────────────────────────────────────────────────────

    //TranslateService,
  ],
};

function initializeKeycloak(keycloak: KeycloakService, injector: Injector) {
  return async () => {
    // Two anonymous surfaces, same reasoning: skip the Keycloak handshake entirely
    // rather than relying on check-sso returning anonymous. The public status page and
    // the developer documentation portal must render with no session, and must not fail
    // to boot when the realm is unreachable (Keycloak down, offline dev, a partner
    // reading the docs from outside the network).
    if (isStatusHost() || window.location.pathname.startsWith("/docs")) {
      return;
    }

    if (!environment.production) {
      await assertKeycloakAvailable();
    }

    rememberKeycloakReturnUrl(window.location, sessionStorage);
    await keycloak.init({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      enableBearerInterceptor: true,
      initOptions: {
        onLoad: environment.production ? "check-sso" : "login-required",
        pkceMethod: "S256",
        responseMode: "query",
        checkLoginIframe: environment.production,
        messageReceiveTimeout: environment.production ? 10000 : 5000,
        silentCheckSsoFallback: environment.production,
        silentCheckSsoRedirectUri: environment.production
          ? window.location.origin + "/assets/silent-check-sso.html"
          : undefined,
        redirectUri: keycloakRedirectUri(window.location.origin),
      },
    });

    const token = await keycloak.getToken();
    if (token) {
      const decoded: any = JSON.parse(atob(token.split(".")[1]));
      const now = Math.floor(Date.now() / 1000);
      const authTime = decoded.auth_time;
      if (now - authTime < 5) {
        const keycloakId =
          keycloak.getKeycloakInstance().idTokenParsed?.sub || "";
        const userService = injector.get(UserApiService);
        userService
          .updateLastLogin({
            "x-api-canal": "web",
            "x-api-requestId": uuidv4(),
            body: keycloakId,
          })
          .subscribe({
            next: () => {},
            error: () => console.error("Error updating last login"),
          });
      }
    }
  };
}

async function assertKeycloakAvailable(): Promise<void> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5000);
  const keycloakUrl = environment.keycloak.url.replace(/\/+$/, "");
  const realm = encodeURIComponent(environment.keycloak.realm);

  try {
    const response = await fetch(
      `${keycloakUrl}/realms/${realm}/.well-known/openid-configuration`,
      {
        cache: "no-store",
        credentials: "omit",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      },
    );

    if (!response.ok) {
      throw new Error(
        `Keycloak availability check failed (${response.status})`,
      );
    }
  } finally {
    window.clearTimeout(timeout);
  }
}
